import React, { useState, useEffect } from 'react';
import { Header, Icon, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTaskContext } from './context/TaskContext';
import * as api from './services/api';

const DelegateCard = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 1.25em;
  margin-bottom: 1em;
`;

const DelegateHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75em;
  margin-bottom: 1em;
`;

const AvatarImg = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #2185d0;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1em;
  font-weight: 600;
`;

const DelegateName = styled.span`
  font-size: 1.1em;
  font-weight: 600;
  color: #333;
`;

const TaskListsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
`;

const TaskListRect = styled.div`
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 0.5em;
  cursor: pointer;
  transition: box-shadow 0.2s, background 0.2s;
  min-width: 70px;

  &:hover {
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    background: #ebebeb;
  }
`;

const TaskListLabel = styled.div`
  font-size: 0.7em;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
  margin-bottom: 0.35em;
`;

const TaskSquaresContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
`;

const TaskSquare = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${props => props.$complete ? '#333' : 'transparent'};
  border: 1.5px solid ${props => props.$complete ? '#333' : '#ccc'};
`;

const DelegationReport = ({ onTaskClick }) => {
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { loadTask } = useTaskContext();

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const data = await api.getDelegationReport();
        setDelegates(data);
      } catch (error) {
        console.error('Error fetching delegation report:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  const handleTaskListClick = (parentUuid) => {
    navigate(`/?p=${parentUuid}`);
    loadTask(parentUuid);
  };

  if (loading) {
    return <Loader active>Loading report...</Loader>;
  }

  return (
    <div>
      <Header as='h2'>
        <Icon>
          <img src="../report-icon.svg" alt="Report" style={{ width: '32px', height: '32px' }} />
        </Icon>
        <Header.Content>Delegation Report</Header.Content>
      </Header>
      {delegates.length > 0 ? (
        delegates.map(delegate => (
          <DelegateCard key={delegate.delegateId}>
            <DelegateHeader>
              {delegate.delegatePicture ? (
                <AvatarImg src={delegate.delegatePicture} alt={delegate.delegateName} />
              ) : (
                <AvatarFallback>
                  {delegate.delegateName?.charAt(0)?.toUpperCase() || '?'}
                </AvatarFallback>
              )}
              <DelegateName>{delegate.delegateName}</DelegateName>
            </DelegateHeader>
            <TaskListsContainer>
              {delegate.taskLists.map(taskList => (
                <TaskListRect
                  key={taskList.parentUuid}
                  onClick={() => handleTaskListClick(taskList.parentUuid)}
                >
                  <TaskListLabel title={taskList.parentLabel}>
                    {taskList.parentLabel}
                  </TaskListLabel>
                  <TaskSquaresContainer>
                    {taskList.tasks.map(task => (
                      <TaskSquare
                        key={task.uuid}
                        $complete={task.status === 2}
                      />
                    ))}
                  </TaskSquaresContainer>
                </TaskListRect>
              ))}
            </TaskListsContainer>
          </DelegateCard>
        ))
      ) : (
        <p>No delegated tasks to display.</p>
      )}
    </div>
  );
};

export default DelegationReport;
